/**
 * ===========================================================================
 *  PROCEDURAL GAIT BUILDER — the thing that makes foot sliding impossible
 * ===========================================================================
 *
 * A locomotion clip has two numbers that MUST agree or the character skates:
 *
 *   1. the stride it declares  (`speed * dur` — what drives the playback rate)
 *   2. the distance its planted foot actually travels backwards through the
 *      body during the stance phase
 *
 * Hand-authored leg angles can never keep those in sync: swing the thigh two
 * degrees wider and the second number moves while the first does not. So we
 * do it the other way round. The ANKLE PATH is authored directly, in metres,
 * in the character's own frame:
 *
 *      contact   z goes from +zF to +zF - E, dead straight, at constant rate
 *      flight    a cubic that leaves and re-enters that line with EXACTLY the
 *                same slope, so the foot is already moving backwards at the
 *                body's speed the instant it touches down
 *
 * and the thigh/shin/foot angles are then SOLVED (analytic two-bone IK in the
 * pelvis's own frame) to put the ankle on that path. The stride is defined as
 * `E / contact`, so by construction:
 *
 *      distance the body covers during stance = speed * contact * dur
 *                                             = (E/contact) * contact
 *                                             = E
 *                                             = distance the foot travels back
 *
 * i.e. the planted foot is nailed to the ground at every playback rate. The
 * animation system blends the ladder on E and contact separately (see
 * `updateLoco`), so the property survives walk<->run<->sprint cross-fades too.
 *
 * Everything here runs ONCE at module load. Nothing allocates at runtime.
 * ===========================================================================
 */
import { BONES, BONE_INDEX } from '../chars/rig.js';
import { compile, sampleChannel } from './curve.js';

const RAD = 180 / Math.PI, DEG = Math.PI / 180;

/* --- rig-derived constants (the canonical 1.80 m rig) -------------------- */
/** A bone as a segment: length + the "declination angle" of its tip offset.
 *  A bone rotated by θ about X points along u(a + θ) with u(g)=(-cos g,-sin g)
 *  in the (y,z) plane — the whole solver is written in that one convention. */
function seg(name) {
  const t = BONES[BONE_INDEX[name]].tip;
  return { L: Math.hypot(t[1], t[2]), a: Math.atan2(-t[2], -t[1]) };
}
const THIGH = seg('thighL'), SHIN = seg('shinL'), FOOT = seg('footL');
const LEG_MAX = (THIGH.L + SHIN.L) * 0.995;
const LEG_MIN = Math.abs(THIGH.L - SHIN.L) + 0.02;
const HIPS_Y = BONES[BONE_INDEX.hips].pos[1];
const TH_OFF = BONES[BONE_INDEX.thighL].pos;          // [0.092, -0.055, 0]

const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const smooth = (t) => t * t * (3 - 2 * t);

/* --- small helpers ------------------------------------------------------- */

/** Row-major 3x3 from an XYZ euler (radians) — matches THREE / pose.js. */
function mat3XYZ(x, y, z, m) {
  const a = Math.cos(x), b = Math.sin(x), c = Math.cos(y), d = Math.sin(y);
  const e = Math.cos(z), f = Math.sin(z);
  m[0] = c * e;              m[1] = -c * f;             m[2] = d;
  m[3] = a * f + b * e * d;  m[4] = a * e - b * f * d;  m[5] = -b * c;
  m[6] = b * f - a * e * d;  m[7] = b * e + a * f * d;  m[8] = a * c;
  return m;
}
/** out = M^T * v  (M orthonormal, so this is the inverse rotation). */
function applyT(m, vx, vy, vz, out) {
  out[0] = m[0] * vx + m[3] * vy + m[6] * vz;
  out[1] = m[1] * vx + m[4] * vy + m[7] * vz;
  out[2] = m[2] * vx + m[5] * vy + m[8] * vz;
  return out;
}
function apply(m, vx, vy, vz, out) {
  out[0] = m[0] * vx + m[1] * vy + m[2] * vz;
  out[1] = m[3] * vx + m[4] * vy + m[5] * vz;
  out[2] = m[6] * vx + m[7] * vy + m[8] * vz;
  return out;
}

/** Piecewise Catmull-Rom through [u, v] control points (clamped ends). */
export function spline(pts, u) {
  const n = pts.length;
  if (u <= pts[0][0]) return pts[0][1];
  if (u >= pts[n - 1][0]) return pts[n - 1][1];
  let i = 0;
  while (i < n - 2 && pts[i + 1][0] <= u) i++;
  const t1 = pts[i][0], t2 = pts[i + 1][0], h = t2 - t1 || 1e-6, s = (u - t1) / h;
  const p1 = pts[i][1], p2 = pts[i + 1][1];
  const i0 = i > 0 ? i - 1 : i, i3 = i + 2 < n ? i + 2 : n - 1;
  const t0 = i > 0 ? pts[i0][0] : t1 - h, t3 = i + 2 < n ? pts[i3][0] : t2 + h;
  const p0 = pts[i0][1], p3 = pts[i3][1];
  const s2 = s * s, s3 = s2 * s;
  const m1 = (p2 - p0) * (h / (t2 - t0 || 1e-6)), m2 = (p3 - p1) * (h / (t3 - t1 || 1e-6));
  return (2 * s3 - 3 * s2 + 1) * p1 + (s3 - 2 * s2 + s) * m1 + (-2 * s3 + 3 * s2) * p2 + (s3 - s2) * m2;
}

/* --- the ankle path ------------------------------------------------------ */

/**
 * Horizontal ankle position, in metres, as a function of cycle phase.
 * `p` = 0 is touch-down. Contact runs 0..c and is exactly linear; the flight
 * cubic re-enters it with a matching slope, which is what removes the
 * touch-down velocity discontinuity that reads as a skid.
 */
export function ankleZ(p, c, E, zF) {
  if (p <= c) return zF - E * (p / c);
  const s = (p - c) / (1 - c);
  const m = -(1 - c) / c;                       // slope, in flight-parameter units
  const s2 = s * s, s3 = s2 * s;
  const g = m * (2 * s3 - 3 * s2 + s) + (-2 * s3 + 3 * s2);
  return zF - E + E * g;
}

/* --- the solver ---------------------------------------------------------- */

const _m = new Float32Array(9), _v = new Float32Array(3), _w = new Float32Array(3);

/**
 * Solve one leg for one instant.
 *
 * @param hx,hy,hz   hips euler (radians)
 * @param rx,ry,rz   root translation (metres)
 * @param side       +1 left, -1 right
 * @param tz,ty      ankle target, metres, in the character's own frame
 *                   (y measured from the ground, z forward)
 * @param footPitch  desired WORLD pitch of the foot bone (radians, + toes down)
 * @param out        [thighX, shinX, footX] radians
 */
export function solveLeg(hx, hy, hz, rx, ry, rz, side, tz, ty, footPitch, out) {
  mat3XYZ(hx, hy, hz, _m);
  // hips joint in character space
  const px = rx, py = ry + HIPS_Y, pz = rz;
  // thigh root, in the hips' own frame, is simply its bind offset
  const ox = TH_OFF[0] * side, oy = TH_OFF[1], oz = TH_OFF[2];
  // ankle target expressed in the hips' frame
  applyT(_m, side * TH_OFF[0] - px, ty - py, tz - pz, _v);   // foot tracks under its own hip
  const dy = _v[1] - oy, dz = _v[2] - oz;
  let r = Math.hypot(dy, dz);
  r = clamp(r, LEG_MIN, LEG_MAX);
  const gD = Math.atan2(-dz, -dy);
  const cosK = clamp((r * r - THIGH.L * THIGH.L - SHIN.L * SHIN.L) / (2 * THIGH.L * SHIN.L), -1, 1);
  const knee = Math.acos(cosK);                                    // >= 0 : bends backwards
  const beta = Math.atan2(SHIN.L * Math.sin(knee), THIGH.L + SHIN.L * Math.cos(knee));
  const g1 = gD - beta;
  const thigh = g1 - THIGH.a;
  const shin = knee + THIGH.a - SHIN.a;
  out[0] = thigh;
  out[1] = shin;
  out[2] = footPitch - hx - thigh - shin;
  return out;
}

/* --- clip construction --------------------------------------------------- */

const _o3 = new Float32Array(3), _e3 = new Float32Array(3);

/**
 * Build the thigh/shin/foot tracks of ONE cycle for the left leg.
 *
 * @param o {{
 *   contact:number,       fraction of the cycle the foot is planted
 *   E:number,             metres the planted ankle travels backwards
 *   front:number,         where touch-down sits, as a fraction of E ahead of hips
 *   hips:number[],        the clip's authored hips rotation track (degrees)
 *   root:number[],        the clip's authored root translation track (metres)
 *   yStrike, yMid, yToe:number,   ankle height (m) at touch-down / mid / toe-off
 *   swing:[u,y][],        ankle height through the flight phase
 *   pitch:[p,deg][],      WORLD foot pitch over the whole cycle (+ toes down)
 *   splay:[ry,rz],        cosmetic thigh twist/roll in degrees
 *   keys:number           number of keys emitted
 * }}
 * @returns {{thigh:number[], shin:number[], foot:number[]}} clip-format tracks
 */
export function legCycle(o) {
  const c = o.contact, E = o.E, zF = E * (o.front ?? 0.42);
  const hipsCh = compile(o.hips, 1, true);        // kept in DEGREES
  const rootCh = compile(o.root, 1, true);
  const nk = o.keys ?? 26;
  const contactPts = [[0, o.yStrike], [0.34, o.yMid], [0.72, o.yMid + 0.012], [1, o.yToe]];
  const ankleC = o.ankle || [[0, 16], [0.3, 0], [0.7, -8], [1, -12]];
  const thigh = [], shin = [], foot = [];
  const sp = o.splay || [0, 0];

  // key times: dense around the two junctions, even elsewhere
  const ts = [];
  for (let i = 0; i < nk; i++) ts.push(i / nk);
  ts.push(c, Math.max(0.004, c - 0.022), Math.min(0.998, c + 0.03), 0.985);
  ts.sort((a, b) => a - b);
  for (let i = ts.length - 1; i > 0; i--) if (ts[i] - ts[i - 1] < 0.004) ts.splice(i, 1);

  for (const t of ts) {
    sampleChannel(hipsCh, t, _e3, 0);
    const hxD = _e3[0], hyD = _e3[1], hzD = _e3[2];
    sampleChannel(rootCh, t, _o3, 0);
    const z = ankleZ(t, c, E, zF);
    const y = t <= c ? spline(contactPts, t / c) : spline(o.swing, (t - c) / (1 - c));
    const pitch = spline(o.pitch, t) * DEG;
    solveLeg(hxD * DEG, hyD * DEG, hzD * DEG, _o3[0], _o3[1], _o3[2], 1, z, y, pitch, _v);
    // While the foot is planted its pitch is dictated in WORLD terms (heel
    // strike, roll, toe-off). In the air that is meaningless — holding a world
    // pitch with the shin folded back would break the ankle — so the flight
    // phase drives the ankle joint directly and the two are cross-faded over
    // the first and last tenth of the swing.
    if (t > c) {
      const u = (t - c) / (1 - c);
      const w = smooth(clamp(u / 0.10, 0, 1)) * smooth(clamp((1 - u) / 0.12, 0, 1));
      _v[2] = _v[2] * (1 - w) + spline(ankleC, u) * DEG * w;
    }
    _v[2] = clamp(_v[2], -34 * DEG, 58 * DEG);   // an ankle is not a hinge on a spring
    thigh.push(+t.toFixed(4), +(_v[0] * RAD).toFixed(2), sp[0], sp[1]);
    shin.push(+t.toFixed(4), +(_v[1] * RAD).toFixed(2), 0, -sp[1] * 0.2);
    foot.push(+t.toFixed(4), +(_v[2] * RAD).toFixed(2), sp[0] * 1.6, 0);
  }
  return { thigh, shin, foot };
}

/**
 * A root-translation track for a gait: the pelvis rides lowest just after each
 * touch-down and highest at the apex of each flight phase (twice per cycle).
 */
export function bob(mean, amp, contact, n = 8) {
  const out = [];
  for (let i = 0; i < n; i++) {
    const t = i / n;
    out.push(+t.toFixed(4), 0, +(mean - amp * Math.cos(4 * Math.PI * (t - contact * 0.5))).toFixed(4), 0);
  }
  return out;
}
